import React from "react";
import { Box, Grid, InputLabel } from "@mui/material";

import AddShiftFields from "./AddShiftFields";
import EditShiftFields from "./EditShiftFields";
import PositionDetailsFields from "./PositionDetailsFields";
import type { PositionFormik, PositionOption, SessionDates } from "./positionForm.types";
import PositionSearchField from "./PositionSearchField";

type AddPositionFormProps = {
  /**
   * "add": wizard step that creates shifts for one or more catalog positions
   * on one or more dates (`values.positions` + `values.positionTimeInfo`).
   * "edit": changes one existing shift (fields straight on `values`).
   */
  mode: "add" | "edit";
  formik: PositionFormik;
  session: SessionDates;
  // Position search (add mode only).
  searchTerm?: string;
  setSearchTerm?: (term: string) => void;
  options?: PositionOption[];
  filteredOptions?: PositionOption[];
  setFilteredOptions?: (options: PositionOption[]) => void;
  /** Called after the "Manage positions in depth" sheet saves (add mode only). */
  afterAddPositions?: () => void;
};

/**
 * Position + shift form used by the create-event wizard ("add") and by the
 * Edit Position modal of the event details ("edit").
 */
const AddPositionForm = ({
  mode,
  formik,
  session,
  searchTerm,
  setSearchTerm,
  options,
  filteredOptions,
  setFilteredOptions,
  afterAddPositions,
}: AddPositionFormProps) => {
  const isEdit = mode === "edit";
  const { values } = formik;

  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Grid container spacing={3} sx={{ width: "100%" }}>
        <Grid item sx={{ width: "100%" }}>
          {isEdit ? (
            <InputLabel>Position Name: {values?.positions?.[0]?.positionName}</InputLabel>
          ) : (
            <PositionSearchField
              {...formik}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              options={options}
              filteredOptions={filteredOptions}
              setFilteredOptions={setFilteredOptions}
            />
          )}
        </Grid>

        {isEdit ? (
          <EditShiftFields formik={formik} session={session} />
        ) : (
          <AddShiftFields formik={formik} session={session} afterAddPositions={afterAddPositions} />
        )}

        <PositionDetailsFields
          formik={formik}
          fullWidthComplexity={isEdit}
          compactTop={values?.currentView === "calendar"}
        />
      </Grid>
    </Box>
  );
};

export default AddPositionForm;
